import type { ProcessingDataClassification } from '@avisoma/shared/dist/ProcessingData';

/**
 * DR-3784: a page's stored `processing.classification.result` is a SNAPSHOT — each tuple keeps
 * the category name as it read when the page was classified. Renaming a category deliberately
 * does not rewrite those tuples (see `propagateCategoryRename` on the server), so the UI cannot
 * trust a stored name: offering it in the category picker sends a name the exact-name lookup in
 * `PATCH /api/document-pages/:id` rejects with a 400, and showing it in the scores table prints a
 * category nobody can find.
 *
 * The tuple's 4th element (the category id) is the stable handle. About 30% of the corpus stores
 * a 3-element tuple with no id, so the stored name stays the fallback for those.
 */
export type ClassificationCatalogue = {
  /**
   * The live catalogue keyed by `String(category.id)` — `pageCategoriesById` off the API.
   *
   * UNDEFINED when the server did not send the field at all (an older server than this
   * frontend). That is not the same as an empty map: an empty map says "no category has any of
   * these ids", which would mark every id-bearing row dead and hide valid approve actions.
   * Undefined means "ids cannot be checked here", and every row falls back to name matching.
   */
  namesById: Record<string, string> | undefined;
  /** The live category names, for id-less tuples that can only be matched by name. */
  liveNames: Set<string>;
};

export type ResolvedClassificationRow = {
  /** What to display and to send back on a save: the live name when one could be resolved. */
  name: string;
  /** The name exactly as the snapshot stored it, for showing what a dead row used to be. */
  storedName: string;
  score: number;
  clusterId?: number;
  categoryId?: number;
  /**
   * Whether this row still points at a category that exists. A non-live row must never be
   * offered as a choice — a save of it 400s.
   */
  live: boolean;
};

/**
 * Resolve a stored ranking against the live catalogue.
 *
 * An UNDEFINED catalogue means "still loading", not "empty" — rows pass through unchanged and
 * live, so nothing flickers or disappears while the categories query is in flight. A catalogue
 * whose `namesById` is undefined resolves every row by name instead (see that field).
 *
 * Malformed rows (a non-array row, or one whose name is not a string) are skipped rather than
 * rendered: `result` comes straight out of jsonb, so no compile-time type proves its shape.
 */
export function resolveClassificationRows(
  result: ProcessingDataClassification | undefined,
  catalogue: ClassificationCatalogue | undefined,
): ResolvedClassificationRow[] {
  if (!Array.isArray(result)) {
    return [];
  }

  return result.flatMap((row) => {
    if (!Array.isArray(row) || typeof row[0] !== 'string') {
      return [];
    }

    const storedName = row[0];
    const score = typeof row[1] === 'number' ? row[1] : 0;
    const clusterId = typeof row[2] === 'number' ? row[2] : undefined;
    const categoryId = typeof row[3] === 'number' ? row[3] : undefined;
    const base = { storedName, score, clusterId, categoryId };

    if (!catalogue) {
      return [{ ...base, name: storedName, live: true }];
    }

    // The id branch needs an id AND a map to look it up in. Without the map (an older server)
    // every row goes to name matching below, id or not. That is a weaker check: a renamed
    // category whose stored name is stale reads as dead (its suggestion is suppressed, never
    // offered wrongly), while a row whose stored name is still live stays live.
    if (categoryId !== undefined && catalogue.namesById) {
      const liveName = catalogue.namesById[String(categoryId)];
      // The id is the stable handle; the stored name is only what the category was called when
      // the snapshot was taken. So the id wins even when the stored name still exists in the
      // live set — the category may simply have been renamed since.
      return [liveName !== undefined ? { ...base, name: liveName, live: true } : { ...base, name: storedName, live: false }];
    }

    return [{ ...base, name: storedName, live: catalogue.liveNames.has(storedName) }];
  });
}

/**
 * The one-click "approve the suggestion" candidate: the rank-1 row's live name. Undefined when
 * rank 1 points at a category that no longer exists — there is nothing safe to approve then, so
 * the caller falls back to its ordinary no-suggestion state.
 *
 * An undefined catalogue (still loading, or the categories query failed and never resolves)
 * offers nothing: a name that was not checked against the live catalogue must never reach an
 * action. Display-only consumers keep `resolveClassificationRows`' passthrough instead.
 */
export function liveSuggestion(
  rows: ResolvedClassificationRow[],
  catalogue: ClassificationCatalogue | undefined,
): string | undefined {
  return catalogue && rows[0]?.live ? rows[0].name : undefined;
}

/**
 * Live names in rank order, deduped — the "Maybe this is the right category?" optgroup. Empty
 * for an undefined catalogue, for the same reason as {@link liveSuggestion}.
 */
export function livePriorityNames(
  rows: ResolvedClassificationRow[],
  catalogue: ClassificationCatalogue | undefined,
): string[] {
  return catalogue ? Array.from(new Set(rows.filter((row) => row.live).map((row) => row.name))) : [];
}
